import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import WanderLogo from './WanderLogo';
import { isValidCountry, normalizeCountryName, searchCountries } from '../../lib/countries';
import { validateCityName } from '../../lib/validateCity';
import { checkDestinationRestriction } from '../../lib/travelSafety';
import { FONTS, BTN } from '../../lib/theme';
import { CUISINE_OPTIONS } from '@wander/shared/utils/settingsConstants.js';
import {
  PACE_OPTIONS,
  BUDGET_OPTIONS,
  COMPANION_OPTIONS,
  CABIN_CLASS_OPTIONS,
  INTEREST_OPTIONS,
  DIETARY_OPTIONS,
  ACCESSIBILITY_OPTIONS,
  SAFETY_CONSIDERATION_OPTIONS,
} from '@wander/shared/utils/onboardingSchema.js';

// Defined outside PersonalizationModal so React sees a stable component reference
// across re-renders — prevents input focus loss on every keystroke
const TagInput = ({ tags, setTags, input, setInput, placeholder, label, onValidate }) => {
  const [validationError, setValidationError] = React.useState('');

  const addTag = (value) => {
    const trimmed = value.trim();
    if (!trimmed || tags.includes(trimmed)) return;

    // Run validation callback if provided (e.g., restricted destination check)
    if (onValidate) {
      const result = onValidate(trimmed);
      if (result && !result.allowed) {
        setValidationError(result.message || `"${trimmed}" cannot be added.`);
        setTimeout(() => setValidationError(''), 5000);
        return;
      }
    }

    setValidationError('');
    setTags([...tags, trimmed]);
    setInput('');
  };

  return (
  <div>
    <label className="block text-lg text-gray-700 mb-3" style={{ fontFamily: "'Batangas', sans-serif", fontWeight: 600 }}>{label}</label>
    {tags.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag, idx) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
          >
            {tag}
            <button
              type="button"
              onClick={() => setTags(tags.filter((_, i) => i !== idx))}
              className="ml-1 hover:text-teal-900"
            >
              ×
            </button>
          </span>
        ))}
      </div>
    )}
    {validationError && (
      <div className="mb-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        ⚠️ {validationError}
      </div>
    )}
    <div className="flex gap-2">
      <input
        type="text"
        value={input}
        onChange={(e) => { setInput(e.target.value); if (validationError) setValidationError(''); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && input.trim()) {
            e.preventDefault();
            addTag(input);
          }
        }}
        placeholder={placeholder}
        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none"
      />
      <button
        type="button"
        onClick={() => addTag(input)}
        className="px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52] text-sm font-medium"
      >
        Add
      </button>
    </div>
  </div>
  );
};

const PersonalizationModal = () => {
  const { showPersonalization, profile, completeOnboarding, skipOnboarding } = useAuth();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Auto-scroll the modal body to the top whenever the step changes.
  // Hope's feedback: "When you click continue, auto pin the top of the next
  // screen so you can see the first question." Previously the scroll position
  // carried over between steps, so longer fields could land mid-screen.
  const bodyScrollRef = useRef(null);
  useEffect(() => {
    if (bodyScrollRef.current) bodyScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Reset loading state after 10 seconds to prevent getting stuck
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        setIsLoading(false);
        setError('Request timed out. Please try again.');
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  // Map of 2-letter codes to full names for backward compatibility
  const codeToName = {
    US: 'United States', UK: 'United Kingdom', CA: 'Canada', AU: 'Australia',
    DE: 'Germany', FR: 'France', JP: 'Japan', IT: 'Italy', ES: 'Spain',
    NL: 'Netherlands', BR: 'Brazil', MX: 'Mexico', IN: 'India', CN: 'China',
    KR: 'South Korea', SG: 'Singapore',
  };

  // Read passport country from canonical preferences JSONB.
  // The legacy profile.passport_country COLUMN was dropped in the unification
  // migration; canonical storage is `profile.preferences.passportCountry`.
  // Supports both single string and JSON-stringified array (legacy migration safety).
  const parsePassportCountries = () => {
    const raw = profile?.preferences?.passportCountry;
    if (!raw) return [];
    let values = [];
    if (Array.isArray(raw)) {
      values = raw;
    } else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) values = parsed;
        else values = [raw];
      } catch {
        values = [raw];
      }
    }
    // Convert any legacy 2-letter codes to full country names
    return values.map(v => {
      if (typeof v === 'string' && v.length === 2 && /^[A-Z]{2}$/.test(v)) {
        return codeToName[v] || v;
      }
      return v;
    });
  };

  // Parse JSON array fields from canonical preferences.
  // After migration, prefs are already arrays in JSONB — this fn handles
  // legacy strings and array passthrough.
  const parseJsonArray = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return [];
  };

  // Form state — keeps snake_case keys for form-internal use; converted to
  // canonical (camelCase) by updateUserProfile's auto-normalizer at save time.
  // ALL reads source from `profile.preferences` (canonical JSONB) instead of
  // dropped legacy columns.
  const _prefs = profile?.preferences || {};
  const [preferences, setPreferences] = useState({
    home_city: _prefs.homeCity || '',
    passport_countries: parsePassportCountries(),
    other_passports: [],
    travel_pace: _prefs.pace || '',
    default_budget: _prefs.budget || '',
    custom_budget_amount: _prefs.customBudgetAmount || '',
    // companions is now a multi-select array; coerce stored string→array for legacy data
    travel_companions: Array.isArray(_prefs.companions)
      ? _prefs.companions
      : (_prefs.companions ? [_prefs.companions] : []),
    safety_considerations: parseJsonArray(_prefs.safetyConsiderations),
    safety_notes: _prefs.safetyConsiderations || '',  // canonical merged into single field
    interests: Array.isArray(_prefs.interests) ? _prefs.interests : [],
    liked_cuisines: Array.isArray(_prefs.likedCuisines) ? _prefs.likedCuisines : [],
    disliked_cuisines: Array.isArray(_prefs.dislikedCuisines) ? _prefs.dislikedCuisines : [],
    dietary_restrictions: Array.isArray(_prefs.dietary) ? _prefs.dietary : [],
    accessibility_needs: Array.isArray(_prefs.accessibilityNeeds) ? _prefs.accessibilityNeeds : [],
    favorite_destinations: parseJsonArray(_prefs.likedPlaces),
    least_favorite_destinations: parseJsonArray(_prefs.avoidPlaces),
    dream_destinations: parseJsonArray(_prefs.likedPlaces),
    loved_places: parseJsonArray(_prefs.likedPlaces),
    avoid_places: parseJsonArray(_prefs.avoidPlaces),
    preferred_cabin_class: (_prefs.cabinClass || 'ECONOMY').toUpperCase(),
    travel_notes: _prefs.travelNotes || '',
    analytics_consent: profile?.analytics_consent || false,
  });

  // Input states for various fields
  const [showOtherPassport, setShowOtherPassport] = useState(false);
  const [otherPassportInput, setOtherPassportInput] = useState('');
  const [passportError, setPassportError] = useState('');
  const [passportSuggestions, setPassportSuggestions] = useState([]);

  const [favoriteInput, setFavoriteInput] = useState('');
  const [beenThereInput, setBeenThereInput] = useState('');
  const [dreamInput, setDreamInput] = useState('');
  const [lovedInput, setLovedInput] = useState('');
  const [avoidInput, setAvoidInput] = useState('');

  const [homeCityError, setHomeCityError] = useState('');
  const [homeCityValidating, setHomeCityValidating] = useState(false);
  const [homeCityValidated, setHomeCityValidated] = useState(false);

  // Available passport countries (common ones for quick selection)
  const passportCountries = [
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  ];

  if (!showPersonalization) return null;

  const totalSteps = 5;

  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const toggleArrayItem = (key, item) => {
    setPreferences(prev => ({
      ...prev,
      [key]: prev[key].includes(item)
        ? prev[key].filter(i => i !== item)
        : [...prev[key], item]
    }));
  };

  // Handle other passport input with validation
  const handleOtherPassportInput = (value) => {
    setOtherPassportInput(value);
    setPassportError('');

    if (value.length >= 2) {
      const suggestions = searchCountries(value);
      setPassportSuggestions(suggestions);
    } else {
      setPassportSuggestions([]);
    }
  };

  const addOtherPassport = (countryName = null) => {
    const input = countryName || otherPassportInput.trim();
    if (!input) return;

    // Validate and normalize the country
    const normalized = normalizeCountryName(input);
    if (normalized) {
      if (!preferences.other_passports.includes(normalized)) {
        updatePreference('other_passports', [...preferences.other_passports, normalized]);
      }
      setOtherPassportInput('');
      setPassportSuggestions([]);
      setPassportError('');
    } else if (!isValidCountry(input)) {
      setPassportError(`"${input}" is not a recognized country. Please check spelling.`);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Filter out empty values and handle special fields
      const cleanedPreferences = Object.fromEntries(
        Object.entries(preferences).filter(([key, v]) => {
          if (key === 'passport_countries' || key === 'other_passports') return false;
          if (key === 'favorite_destinations' || key === 'least_favorite_destinations') return false;
          if (key === 'dream_destinations' || key === 'loved_places' || key === 'avoid_places') return false;
          if (key === 'safety_considerations') return false; // Handle separately
          return v !== '' && v !== null && !(Array.isArray(v) && v.length === 0);
        })
      );

      // Build passport countries array including "Other" inputs
      const allPassports = [
        ...preferences.passport_countries,
        ...preferences.other_passports
      ];

      if (allPassports.length > 0) {
        cleanedPreferences.passport_country = JSON.stringify(allPassports);
      }

      // Store safety considerations as JSON array
      if (preferences.safety_considerations.length > 0) {
        cleanedPreferences.safety_considerations = JSON.stringify(preferences.safety_considerations);
      }

      // Store favorite and been-there destinations as arrays (Postgres TEXT[])
      if (preferences.favorite_destinations.length > 0) {
        cleanedPreferences.favorite_destinations = preferences.favorite_destinations;
      }
      if (preferences.least_favorite_destinations.length > 0) {
        cleanedPreferences.least_favorite_destinations = preferences.least_favorite_destinations;
      }
      // Store dream/loved/avoid destinations as arrays
      if (preferences.dream_destinations.length > 0) {
        cleanedPreferences.dream_destinations = preferences.dream_destinations;
      }
      if (preferences.loved_places.length > 0) {
        cleanedPreferences.loved_places = preferences.loved_places;
      }
      if (preferences.avoid_places.length > 0) {
        cleanedPreferences.avoid_places = preferences.avoid_places;
      }

      // Travel notes is a simple text field
      if (preferences.travel_notes && preferences.travel_notes.trim()) {
        cleanedPreferences.travel_notes = preferences.travel_notes.trim();
      }

      // Add consent date if consented
      if (cleanedPreferences.analytics_consent) {
        cleanedPreferences.analytics_consent_date = new Date().toISOString();
      }

      console.log('Saving preferences:', cleanedPreferences);
      await completeOnboarding(cleanedPreferences);
    } catch (err) {
      setError('We were temporarily unable to save your preferences, please try again later. If this error persists, please reach out to hello@arrivotravel.ai');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      await skipOnboarding();
    } catch (err) {
      // Error reported to Sentry via shared reportError
    } finally {
      setIsLoading(false);
    }
  };

  // Option components
  const SelectButton = ({ selected, onClick, children, className = '' }) => (
    <button
      type="button"
      onClick={onClick}
      className={`${BTN.pill(selected)} ${className}`}
      style={FONTS.body}
    >
      {children}
    </button>
  );

  const MultiSelectChip = ({ selected, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={BTN.pill(selected)}
      style={FONTS.body}
    >
      {children}
    </button>
  );

  // TagInput is now defined outside PersonalizationModal (top of file) to prevent focus loss

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={FONTS.body}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border-2 border-teal-200 max-h-[90vh] flex flex-col">
        {/* Header with Wander Branding */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-8 py-6 text-center shrink-0">
          <div className="flex justify-center mb-2">
            <WanderLogo size="md" showText={true} showTagline={true} textColor="text-white" />
          </div>
          <h2 className="text-xl font-bold text-white" style={FONTS.headingBold}>Personalize Your Experience</h2>
          <p className="text-teal-100 text-sm mt-1" style={FONTS.body}>
            Help us create better itineraries for you
          </p>
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-4">
            {[...Array(totalSteps)].map((_, i) => (
              <div
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-white' : 'bg-teal-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div ref={bodyScrollRef} className="px-8 py-8 max-h-[60vh] overflow-y-auto min-h-0">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Basics */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Where are you based? (Home city)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={preferences.home_city}
                    onChange={(e) => {
                      updatePreference('home_city', e.target.value);
                      setHomeCityError('');
                      setHomeCityValidated(false);
                    }}
                    onBlur={async (e) => {
                      const input = e.target.value.trim();
                      if (!input) {
                        setHomeCityError('');
                        setHomeCityValidated(false);
                        return;
                      }
                      setHomeCityValidating(true);
                      setHomeCityError('');
                      try {
                        const result = await validateCityName(input);
                        if (result?.valid) {
                          // Auto-correct to proper name
                          if (result.correctedName && result.correctedName !== input) {
                            updatePreference('home_city', result.correctedName);
                          }
                          setHomeCityValidated(true);
                          setHomeCityError('');
                        } else if (result?.error === 'validation_unavailable') {
                          // Backend down — don't block the user
                          setHomeCityValidated(false);
                          setHomeCityError('');
                        } else {
                          setHomeCityError(`"${input}" doesn't appear to be a valid city`);
                          setHomeCityValidated(false);
                        }
                      } catch {
                        // Network error — don't block
                        setHomeCityValidated(false);
                      }
                      setHomeCityValidating(false);
                    }}
                    placeholder="e.g., New York, London, Tokyo"
                    className={`w-full px-4 py-3 pr-10 border-2 rounded-xl focus:outline-none ${
                      homeCityError
                        ? 'border-red-300 focus:border-red-500'
                        : homeCityValidated
                        ? 'border-green-300 focus:border-green-500'
                        : 'border-gray-200 focus:border-teal-500'
                    }`}
                  />
                  {/* Status indicator */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {homeCityValidating && (
                      <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                    )}
                    {!homeCityValidating && homeCityValidated && (
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {!homeCityValidating && homeCityError && (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                        <path strokeLinecap="round" strokeWidth="2" d="M12 8v4m0 4h.01" />
                      </svg>
                    )}
                  </div>
                </div>
                {homeCityError && (
                  <p className="text-xs text-red-500 mt-1">{homeCityError}</p>
                )}
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-2" style={FONTS.headingSemibold}>
                  Passport country/countries (for visa recommendations)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Select all passports you hold - this helps with visa-free travel recommendations
                </p>
                <div className="flex flex-wrap gap-2">
                  {passportCountries.map(country => (
                    <MultiSelectChip
                      key={country.code}
                      selected={preferences.passport_countries.includes(country.name)}
                      onClick={() => toggleArrayItem('passport_countries', country.name)}
                    >
                      {country.flag} {country.name}
                    </MultiSelectChip>
                  ))}
                  <MultiSelectChip
                    selected={showOtherPassport}
                    onClick={() => setShowOtherPassport(!showOtherPassport)}
                  >
                    Other
                  </MultiSelectChip>
                </div>

                {/* Custom passport input with validation */}
                {showOtherPassport && (
                  <div className="mt-3 space-y-2">
                    {preferences.other_passports.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {preferences.other_passports.map((country, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                          >
                            {country}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = preferences.other_passports.filter((_, i) => i !== idx);
                                updatePreference('other_passports', updated);
                              }}
                              className="ml-1 hover:text-teal-900"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="relative">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={otherPassportInput}
                          onChange={(e) => handleOtherPassportInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addOtherPassport();
                            }
                          }}
                          placeholder="Type country name..."
                          className={`flex-1 px-4 py-3 border-2 rounded-xl focus:border-teal-500 focus:outline-none ${
                            passportError ? 'border-red-300' : 'border-gray-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => addOtherPassport()}
                          className="px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52] text-sm font-medium"
                        >
                          Add
                        </button>
                      </div>
                      {/* Suggestions dropdown */}
                      {passportSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {passportSuggestions.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => addOtherPassport(country.name)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-teal-50 text-gray-700"
                            >
                              {country.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {passportError && (
                      <p className="text-xs text-red-500">{passportError}</p>
                    )}
                  </div>
                )}

                {(preferences.passport_countries.length > 0 || preferences.other_passports.length > 0) && (
                  <p className="text-xs text-teal-600 mt-2">
                    Selected: {[
                      ...preferences.passport_countries,
                      ...preferences.other_passports
                    ].join(', ')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Travel Preferences */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Who do you usually travel with?
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Pick a base type plus any modifiers (kids, elderly, pets, etc.)
                </p>
                <div className="flex flex-wrap gap-2">
                  {COMPANION_OPTIONS.map(option => {
                    const current = Array.isArray(preferences.travel_companions)
                      ? preferences.travel_companions
                      : (preferences.travel_companions ? [preferences.travel_companions] : []);
                    const selected = current.includes(option.value);
                    const baseValues = COMPANION_OPTIONS.filter(o => o.base).map(o => o.value);
                    return (
                      <SelectButton
                        key={option.value}
                        selected={selected}
                        onClick={() => {
                          let updated;
                          if (selected) {
                            updated = current.filter(c => c !== option.value);
                          } else if (option.base) {
                            updated = [...current.filter(c => !baseValues.includes(c)), option.value];
                          } else {
                            updated = [...current, option.value];
                          }
                          updatePreference('travel_companions', updated);
                        }}
                      >
                        {option.icon ? `${option.icon} ` : ''}{option.label}
                      </SelectButton>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  What's your travel pace?
                </label>
                <div className="flex flex-wrap gap-3">
                  {PACE_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updatePreference('travel_pace', option.value)}
                      className={`${BTN.card(preferences.travel_pace === option.value)} flex-1 min-w-[120px] text-center`}
                      style={FONTS.body}
                    >
                      <div className="text-base">{option.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Default budget level?
                </label>
                <div className="flex flex-wrap gap-2">
                  {BUDGET_OPTIONS.map(option => (
                    <SelectButton
                      key={option.value}
                      selected={preferences.default_budget === option.value}
                      onClick={() => {
                        updatePreference('default_budget', option.value);
                        if (option.value !== 'custom') updatePreference('custom_budget_amount', '');
                      }}
                      title={option.description}
                    >
                      {option.label}
                    </SelectButton>
                  ))}
                </div>
                {preferences.default_budget === 'custom' && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="50"
                        placeholder="Total trip budget"
                        value={preferences.custom_budget_amount}
                        onChange={(e) => updatePreference('custom_budget_amount', e.target.value)}
                        className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a7d8db] focus:border-[#1d8e85]"
                      />
                      <span className="text-xs text-gray-400">for your entire trip</span>
                    </div>
                    {preferences.custom_budget_amount && parseFloat(preferences.custom_budget_amount) > 0 && parseFloat(preferences.custom_budget_amount) < 200 && (
                      <p className="text-xs text-amber-600 mt-2">That's quite low — you can always adjust this per trip later.</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Preferred cabin class
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'ECONOMY', label: 'Economy' },
                    { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
                    { value: 'BUSINESS', label: 'Business' },
                    { value: 'FIRST', label: 'First' },
                  ].map(option => (
                    <SelectButton
                      key={option.value}
                      selected={preferences.preferred_cabin_class === option.value}
                      onClick={() => updatePreference('preferred_cabin_class', option.value)}
                    >
                      {option.label}
                    </SelectButton>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Interests & Needs */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Interests (Select all that apply)
                </label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map(option => (
                    <MultiSelectChip
                      key={option.value}
                      selected={preferences.interests.includes(option.value)}
                      onClick={() => toggleArrayItem('interests', option.value)}
                    >
                      {option.label}
                    </MultiSelectChip>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Favorite Cuisines
                </label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_OPTIONS.map(cuisine => (
                    <MultiSelectChip
                      key={cuisine}
                      selected={preferences.liked_cuisines.includes(cuisine)}
                      onClick={() => toggleArrayItem('liked_cuisines', cuisine)}
                    >
                      {cuisine}
                    </MultiSelectChip>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Cuisines to Avoid
                </label>
                <div className="flex flex-wrap gap-2">
                  {CUISINE_OPTIONS.map(cuisine => (
                    <MultiSelectChip
                      key={cuisine}
                      selected={preferences.disliked_cuisines.includes(cuisine)}
                      onClick={() => toggleArrayItem('disliked_cuisines', cuisine)}
                    >
                      {cuisine}
                    </MultiSelectChip>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-500 italic bg-gray-50 rounded-lg px-3 py-2">
                This section may include health, dietary, religious, or identity-related info. It's optional, never shared, and only used to personalize your trips.
              </p>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Dietary restrictions (if any)
                </label>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_OPTIONS.map(option => (
                    <MultiSelectChip
                      key={option.value}
                      selected={preferences.dietary_restrictions.includes(option.value)}
                      onClick={() => {
                        if (option.value === 'none') {
                          updatePreference('dietary_restrictions',
                            preferences.dietary_restrictions.includes('none') ? [] : ['none']
                          );
                        } else {
                          const diet = option.value;
                          const newRestrictions = preferences.dietary_restrictions.includes(diet)
                            ? preferences.dietary_restrictions.filter(d => d !== diet)
                            : [...preferences.dietary_restrictions.filter(d => d !== 'None'), diet];
                          updatePreference('dietary_restrictions', newRestrictions);
                        }
                      }}
                    >
                      {option.label}
                    </MultiSelectChip>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Accessibility needs (if any)
                </label>
                <div className="flex flex-wrap gap-2">
                  {ACCESSIBILITY_OPTIONS.map(option => (
                    <MultiSelectChip
                      key={option.value}
                      selected={preferences.accessibility_needs.includes(option.value)}
                      onClick={() => toggleArrayItem('accessibility_needs', option.value)}
                    >
                      {option.label}
                    </MultiSelectChip>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg text-gray-700 mb-2" style={FONTS.headingSemibold}>
                  Are there any safety or comfort considerations we should know about?
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  We'll prioritize destinations and accommodations known to be welcoming and safe.
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {SAFETY_CONSIDERATION_OPTIONS.map(option => (
                    <MultiSelectChip
                      key={option.value}
                      selected={preferences.safety_considerations.includes(option.value)}
                      onClick={() => toggleArrayItem('safety_considerations', option.value)}
                    >
                      {option.label}
                    </MultiSelectChip>
                  ))}
                </div>
                <input
                  type="text"
                  value={preferences.safety_notes}
                  onChange={(e) => updatePreference('safety_notes', e.target.value)}
                  placeholder="Any other safety or comfort notes (optional)"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none" style={FONTS.body}
                />
              </div>
            </div>
          )}

          {/* Step 4: Destinations & Notes */}
          {step === 4 && (
            <div className="space-y-6">
              <TagInput
                tags={preferences.dream_destinations}
                setTags={(tags) => updatePreference('dream_destinations', tags)}
                input={dreamInput}
                setInput={setDreamInput}
                label="Dream Destinations"
                placeholder="Places you dream of visiting, e.g., Tokyo, Bali..."
                onValidate={(dest) => checkDestinationRestriction(dest)}
              />

              <TagInput
                tags={preferences.loved_places}
                setTags={(tags) => updatePreference('loved_places', tags)}
                input={lovedInput}
                setInput={setLovedInput}
                label="Places You've Loved"
                placeholder="Destinations you've been to and loved, e.g., Barcelona..."
                onValidate={(dest) => checkDestinationRestriction(dest)}
              />

              <TagInput
                tags={preferences.avoid_places}
                setTags={(tags) => updatePreference('avoid_places', tags)}
                input={avoidInput}
                setInput={setAvoidInput}
                label="Places to Avoid"
                placeholder="Places you want to avoid, e.g., Las Vegas..."
                onValidate={(dest) => checkDestinationRestriction(dest)}
              />

              <div>
                <label className="block text-lg text-gray-700 mb-2" style={FONTS.headingSemibold}>
                  Anything else we should know about how you like to travel?
                </label>
                <textarea
                  value={preferences.travel_notes}
                  onChange={(e) => updatePreference('travel_notes', e.target.value)}
                  placeholder="e.g., I prefer morning activities, hate crowded places, always need good coffee nearby..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none resize-none" style={FONTS.body}
                />
              </div>
            </div>
          )}

          {/* Step 5: Privacy & Analytics */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="p-5 bg-gray-50 rounded-xl">
                <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>
                  Help improve arrivo
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  We use anonymous analytics to understand how people use arrivo
                  and make it better. Your data is never sold or shared.
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.analytics_consent}
                    onChange={(e) => updatePreference('analytics_consent', e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to anonymous analytics to help improve arrivo
                  </span>
                </label>
              </div>

              <div className="p-5 bg-teal-50 rounded-xl">
                <h3 className="text-lg text-teal-900 mb-2" style={FONTS.headingSemibold}>
                  You're all set!
                </h3>
                <p className="text-sm text-teal-700">
                  We'll use your preferences to create personalized itineraries.
                  You can update these anytime in your profile.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <div>
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="text-gray-600 hover:text-gray-800 font-medium"
                style={FONTS.body}
                disabled={isLoading}
              >
                ← Back
              </button>
            ) : (
              <button
                onClick={handleSkip}
                className="text-gray-500 hover:text-gray-700 font-medium"
                style={FONTS.body}
                disabled={isLoading}
              >
                Skip for now
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {step < totalSteps ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-8 py-3 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-colors font-bold text-base"
                style={FONTS.bodyBold}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={isLoading}
                className="px-8 py-3 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-colors font-bold text-base disabled:opacity-50"
                style={FONTS.bodyBold}
              >
                {isLoading ? 'Saving...' : 'Complete Setup'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalizationModal;
